import {
  type ComponentType,
  type ReactNode,
  type Ref,
  useState,
  useCallback,
} from "react";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../Icons/Icon";
import { Spinner } from "../Feedback/Spinner";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";
import { useIsFormItemError } from "../FormLayout/FormItemErrorProvider";
import { useFormItemWarningMark } from "./Internal/FormItemWarningMark";
import { useTypingPause } from "../FormLayout/Internal/typingPause";
import { useControlBarSlot } from "../PageLayout/Internal/ControlBarContext";
import { useControlPresentation } from "./Internal/useControlPresentation";
import {
  ADORNMENT_COLOR,
  inputBaseStyle,
  underlineStyle,
  underlineWrapper,
  boxedBase,
  boxedField,
  boxedWrapper,
} from "./Internal/textInputStyles";
import {
  inputWidthClasses as widthClasses,
  type InputWidth,
} from "./inputWidth";

/**
 * Outcome of a TextInput end-button `onClick` (inline-edit submit). Returning
 * `InlineEditResult.Commit` — or simply falling through with no return — marks
 * the input clean (the edit was accepted); returning
 * `InlineEditResult.KeepEditing` leaves it dirty so the user can fix and retry.
 * Replaces the older `void | false` contract.
 */
export const InlineEditResult = {
  Commit: "commit",
  KeepEditing: "keep-editing",
} as const;
export type InlineEditResult =
  (typeof InlineEditResult)[keyof typeof InlineEditResult];

interface EndButton {
  label?: string;
  /** Called with the current input value. Unless it returns (or resolves to) `InlineEditResult.KeepEditing`, the input resets to "not dirty". */
  onClick: (
    value: string,
  ) => void | InlineEditResult | Promise<void | InlineEditResult>;
  icon?: ComponentType<{ className?: string }>;
  variant?: "secondary" | "primary";
  loading?: boolean;
  disabled?: boolean;
}

interface TextInputProps {
  defaultValue?: string | undefined;
  value?: string | undefined;
  placeholder?: string | undefined;
  onChange?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  onKeyDown?: ((e: React.KeyboardEvent<HTMLInputElement>) => void) | undefined;
  onFocus?: ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined;
  onBlur?: ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined;
  mono?: boolean | undefined;
  width?: InputWidth | undefined;
  disabled?: boolean | undefined;
  startIcon?: ComponentType<{ className?: string }>;
  endIcon?: ComponentType<{ className?: string }>;
  /** Content in the field's left slot — a currency symbol, a unit. Use
   *  `startIcon` when it's an icon; this is for anything else. */
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  /** Button rendered inside the input, flush against the right edge. */
  endButton?: EndButton | undefined;
  allowClear?: boolean | undefined;
  loading?: boolean | undefined;
  type?: string | undefined;
  /** Forwarded to the underlying `<input>` — needed for form submission and
   *  password-manager field matching (e.g. a login form). */
  name?: string | undefined;
  /**
   * Forwarded to the underlying `<input>`. Fields default to `data-1p-ignore`
   * (admin tools don't want autofill noise); setting a real autocomplete token
   * (e.g. "email", "current-password") opts the field back into password
   * managers and browser autofill — use it on customer login / sign-up.
   */
  autoComplete?: string | undefined;
  /** Forwarded to the underlying `<input>` to cap the value length. */
  maxLength?: number | undefined;
  /** Content shown in a floating panel below the input when focused. */
  focusHint?: ReactNode | undefined;
  /** Visual style. "ghost" renders a translucent input for use on colored backgrounds (e.g. navbar). */
  variant?: "default" | "ghost" | undefined;
  /**
   * Forwarded to the underlying `<input>`. Needed by controls that manage the
   * caret themselves (e.g. formatted phone / currency fields).
   */
  ref?: Ref<HTMLInputElement> | undefined;
  /** Forwarded to the underlying <input> for test selection. */
  "data-testid"?: string | undefined;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function TextInput({
  defaultValue,
  value,
  placeholder,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  mono,
  width,
  disabled,
  startIcon,
  endIcon,
  startAdornment,
  endAdornment,
  endButton,
  allowClear,
  loading,
  type = "text",
  name,
  autoComplete,
  maxLength,
  focusHint,
  variant = "default",
  ref,
  "data-testid": dataTestid,
}: TextInputProps) {
  const markDirty = useMarkDialogDirty();
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [baselineValue, setBaselineValue] = useState(defaultValue ?? "");
  const [endButtonLoading, setEndButtonLoading] = useState(false);
  // Drives the warning explanation open while the field is being edited —
  // see `statusIcon`. Only read when a FormItem supplied an explanation.
  const [focused, setFocused] = useState(false);
  // Typing `22` into a height field passes through `2`, which trips
  // `minSizeWarning` — so an explanation keyed to focus alone flaps open between
  // two keystrokes. It waits for the pause; the inline message still paints at
  // once, which is the deliberate warning-vs-error asymmetry (see FormItem).
  const { settled, noteKeystroke } = useTypingPause();
  const currentValue = value ?? internalValue;
  const ghost = variant === "ghost";
  const isError = useIsFormItemError();
  // standard (Cargo / dashboard) is an underline-only look. The ghost variant
  // (navbar) keeps its own styling and opts out of standard.
  const standard = !ghost;
  // standard defaults to a dense boxed field; the spacious underline is opt-in for
  // low-density customer flows (quote/booking, login).
  const presentation = useControlPresentation();
  const boxed = standard && presentation === "boxed";
  // Inside a control/filter bar, standard underline fields shrink to a compact size
  // so they line up with sibling controls instead of the large Cargo form size.
  const inControlBar = useControlBarSlot() !== false;
  const compact = standard && !boxed && inControlBar;
  // Inside a filter/control bar the boxed field tightens (h-8 + less padding).
  const boxedCompact = boxed && inControlBar;
  // Ghost is `dark`-scoped (see wrapperClasses), so the same semantic color
  // roles resolve to the dark-surface values — no white-opacity text.
  const iconColor = ADORNMENT_COLOR;
  const effectiveStartAdornment = startIcon ? (
    <Icon icon={startIcon} size="sm" color={iconColor} />
  ) : (
    startAdornment
  );
  // In-field icons (loading spinner, warning mark) are sized to 1em of the
  // field's own text so they always match the value — small in a dense filter
  // bar, large in a spacious quote form — and pair with plain (icon-less)
  // helper/error text below. Consistent across every ui-core text field.
  const inputTextSizeClass = ghost
    ? "text-xs"
    : boxed
      ? boxedCompact
        ? "text-xs"
        : "text-sm"
      : compact
        ? "text-sm"
        : "text-xl";
  // The branded loader (blue + gold orbiting dots), sized to roughly match the
  // field's value text so it sits on the value line.
  const spinnerPx = ghost
    ? 16
    : boxed
      ? boxedCompact
        ? 14
        : 16
      : compact
        ? 15
        : 20;
  const loadingSpinner = loading ? <Spinner size={spinnerPx} /> : undefined;
  // An orange mark on the value line for a warning, sized to the field's text
  // and shown even when an end button is present (rendered just before it). Only
  // a warning gets one: its message below is plain foreground text, so the glyph
  // is the only thing saying "warning". An error needs no glyph — its underline
  // and its message are both already red.
  const warningMark = useFormItemWarningMark({
    focused: focused && settled,
    loading,
  });
  // Sized here rather than inside the mark: the glyph is 1em, and the em it
  // should match is this field's value text, not the wrapper it lands in.
  const statusIcon = warningMark ? (
    <span className={`${inputTextSizeClass} inline-flex`}>{warningMark}</span>
  ) : undefined;
  const effectiveEndAdornment =
    loadingSpinner ??
    statusIcon ??
    (endIcon ? (
      <Icon icon={endIcon} size="sm" color={iconColor} />
    ) : (
      endAdornment
    ));
  const hasAdornments = !!(
    effectiveStartAdornment ||
    effectiveEndAdornment ||
    endButton ||
    allowClear
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      markDirty?.();
      noteKeystroke();
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    },
    [onChange, value, markDirty, noteKeystroke],
  );

  const handleClear = useCallback(() => {
    markDirty?.();
    const syntheticEvent = {
      target: { value: "" },
      currentTarget: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>;
    if (value === undefined) {
      setInternalValue("");
    }
    onChange?.(syntheticEvent);
  }, [onChange, value, markDirty]);

  // Ghost floors dim text at `muted`, not `faint`: the navy nav chip is much
  // lighter than the true dark-mode surfaces the dark `faint` value is tuned
  // for, so faint (#52525b) drops below visibility there.
  const baseInputClasses = ghost
    ? `text-xs font-medium text-foreground placeholder:text-content-muted focus:outline-none bg-transparent ${mono ? "font-mono" : ""}`
    : boxed
      ? boxedBase(!!mono, boxedCompact)
      : inputBaseStyle(!!mono, compact);
  const disabledClasses = disabled ? "opacity-40 pointer-events-none" : "";
  const showClear = allowClear && currentValue.length > 0;

  const wrapperClasses = ghost
    ? `dark group relative flex h-7 items-center rounded-md border border-white/20 bg-white/10 transition-colors focus-within:bg-white/20 ${disabledClasses} ${width ? widthClasses[width] : "w-full"}`
    : boxed
      ? `group relative flex items-center pr-1 ${boxedWrapper(isError, boxedCompact)} ${disabledClasses} ${width ? widthClasses[width] : "w-full"}`
      : `group relative flex items-baseline bg-transparent ${compact ? "pt-1" : "pt-2"} transition-colors ${underlineWrapper(isError)} ${disabledClasses} ${width ? widthClasses[width] : "w-full"}`;

  // ONE return for both the adorned and the plain field. They used to be two
  // separate returns with different child shapes, so when an adornment
  // appeared dynamically — the FormItem status icon revealing ~2s into
  // typing, or a loading spinner — React reconciled across branches and
  // REMOUNTED the <input>, dropping focus (and any in-flight keystrokes)
  // mid-word. The child slots below are stable; only classes change.
  // The plain path defaults to `w-full` for the same reason the adorned one
  // does (ENG-6061): inside FormItem's beside-mode flex wrapper a width-less div
  // shrinks to the input's intrinsic width, so the field ends up narrower than
  // its grid column and its value gets clipped.
  const wrapperClass = hasAdornments
    ? wrapperClasses
    : `group relative ${width ? widthClasses[width] : "w-full"}`;

  const inputClass = hasAdornments
    ? `${baseInputClasses} ${boxed ? `h-full min-w-0 flex-1 ${boxedCompact ? "px-1.5" : "px-3"}` : standard ? "min-w-0 flex-1 px-0" : "h-full min-w-0 flex-1 px-2.5"} ${effectiveStartAdornment ? (standard && !boxed ? "pl-2" : "pl-1.5") : ""} ${showClear || effectiveEndAdornment ? "pr-1" : ""}`
    : ghost
      ? `dark ${baseInputClasses} w-full rounded-md px-3 transition-colors ${disabledClasses} h-7 border border-white/20 bg-white/10 focus:bg-white/20`
      : boxed
        ? `${baseInputClasses} w-full ${boxedCompact ? "px-1.5" : "px-3"} ${disabledClasses} ${boxedField(isError, boxedCompact)}`
        : `${baseInputClasses} w-full px-0 ${compact ? "pt-1" : "pt-2"} transition-colors ${disabledClasses} ${underlineStyle(isError)}`;

  return (
    <div className={wrapperClass}>
      {effectiveStartAdornment && (
        <span
          // Boxed: the icon takes the box's left gutter (matching the px-3 a
          // plain field insets its text) so it isn't jammed against the
          // border. Underline: flush left, aligned to the underline edge.
          className={`flex shrink-0 items-center ${
            boxed
              ? `self-center ${boxedCompact ? "pl-1.5" : "pl-3"}`
              : standard
                ? "self-center pl-0"
                : "pl-2.5"
          } ${ghost ? "text-content-muted" : "text-content-faint"}`}
        >
          {effectiveStartAdornment}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        defaultValue={value === undefined ? defaultValue : undefined}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        disabled={disabled || endButtonLoading}
        maxLength={maxLength}
        name={name}
        autoComplete={autoComplete}
        {...(autoComplete === undefined && { "data-1p-ignore": true })}
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        className={inputClass}
      />
      {effectiveEndAdornment && !endButton && (
        <span
          // Boxed: match the left gutter on the right (the wrapper already
          // adds pr-1, so the span tops it up to the box's px-3 inset).
          className={`flex shrink-0 items-center ${
            boxed
              ? `self-center ${boxedCompact ? "pr-0.5" : "pr-2"}`
              : standard
                ? "self-center pr-0"
                : "pr-2.5"
          } ${ghost ? "text-content-muted" : "text-content-faint"}`}
        >
          {effectiveEndAdornment}
        </span>
      )}
      {/* An end button owns the end slot, so surface the status icon just
            before it (a validated field with an action still flags its state). */}
      {endButton && statusIcon && (
        <span className="flex shrink-0 items-center self-center pr-1">
          {statusIcon}
        </span>
      )}
      {endButton && (
        <button
          type="button"
          onClick={() => {
            const result = endButton.onClick(currentValue);
            if (result instanceof Promise) {
              setEndButtonLoading(true);
              result
                .then((v) => {
                  if (v !== InlineEditResult.KeepEditing)
                    setBaselineValue(currentValue);
                })
                .finally(() => setEndButtonLoading(false));
            } else if (result !== InlineEditResult.KeepEditing) {
              setBaselineValue(currentValue);
            }
          }}
          disabled={
            endButton.disabled ||
            endButton.loading ||
            endButtonLoading ||
            disabled ||
            currentValue === baselineValue
          }
          className={
            // standard: no box/divider — a Cargo action sitting on the underline.
            // Filled blue when active (primary or dirty), blue text otherwise.
            // The ghost branch (navbar) keeps its own styling.
            standard
              ? `my-1 flex shrink-0 cursor-pointer items-center gap-1.5 self-center rounded-md py-1 ${endButton.label ? "px-3" : "px-2"} text-xs font-semibold transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                  endButton.variant === "primary" ||
                  currentValue !== baselineValue
                    ? "bg-brand text-white hover:bg-brand/85"
                    : "text-brand-text hover:bg-brand/10"
                }`
              : `flex h-full shrink-0 cursor-pointer items-center gap-1.5 rounded-r-[4px] border-l ${endButton.label ? "px-3" : "px-2"} border-white/20 bg-white/15 text-xs font-semibold text-content-secondary transition-colors hover:bg-white/25 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40`
          }
        >
          {endButton.loading || endButtonLoading ? (
            <Spinner size={14} onColor />
          ) : (
            endButton.icon && <Icon icon={endButton.icon} size="xs" />
          )}
          {endButton.label}
        </button>
      )}
      {showClear && (
        <button
          type="button"
          aria-label="Clear input"
          tabIndex={-1}
          onClick={handleClear}
          onMouseDown={(e) => e.preventDefault()}
          className={`flex shrink-0 items-center pr-2 transition-colors hover:text-content-secondary ${ghost ? "text-content-muted" : "text-content-faint"}`}
        >
          <Icon icon={XIcon} size="sm" />
        </button>
      )}
      {focusHint && <FocusHintPanel_>{focusHint}</FocusHintPanel_>}
    </div>
  );
}

function FocusHintPanel_({ children }: { children: ReactNode }) {
  return (
    <div
      className="absolute top-full left-0 z-50 mt-1 hidden w-max max-w-sm rounded-md border border-border bg-card p-3 text-xs text-content-muted shadow-floating group-focus-within:block"
      onMouseDown={(e) => e.preventDefault()}
    >
      {children}
    </div>
  );
}
